import { useState, useEffect, useCallback, useMemo, useRef, lazy, Suspense } from "react";
import { useTripleTap } from "@/hooks/useTripleTap";
import { Link, useLocation } from "wouter";
import { useQuery } from "@tanstack/react-query";
import { getAllArtists } from "@/lib/artists";
import { apiFetch } from "@/lib/apiFetch";
import { buildInfoContract, eventsUpcomingContract } from "@shared/contracts";
import { useStudioConfig } from "@/hooks/use-studio-config";
import { PrefetchLink } from "@/components/PrefetchLink";
import { OptimizedImage } from "@/components/OptimizedImage";

interface BuildInfo {
  buildId: string;
  version: string;
  date: string;
}
import { Facebook, Instagram, Menu, ChevronDown, Home, Users, CalendarDays, Mail, MailOpen, ArrowUp, MapPin, Clock, Phone, Info, Sparkles, ShoppingBag, Activity, Palette, DollarSign, ImageIcon, Heart, PenLine, Flame, Droplets, Smartphone, LogIn, X, HelpCircle, PlayCircle, Presentation } from "lucide-react";
const GlobalAdminSearch = lazy(() =>
  import("@/components/GlobalAdminSearch").then(m => ({ default: m.GlobalAdminSearch }))
);
const AdminCommandPaletteButton = lazy(() =>
  import("@/components/AdminCommandPaletteButton").then(m => ({ default: m.AdminCommandPaletteButton }))
);
import { usePWAInstall } from "@/hooks/usePWAInstall";

const ARTIST_ICONS: Record<string, React.ComponentType<{ size?: number; className?: string; style?: React.CSSProperties }>> = {
  "kathleen": PenLine,
  "constance-masters": Flame,
  "mikayla-alderton": Droplets,
};
import { useSiteConfig } from "@/lib/SiteConfigContext";
import { useFeatureFlags } from "@/lib/FeatureFlagContext";
import { FeatureFlagZone } from "@/components/FeatureFlagZone";
import { motion, AnimatePresence } from "framer-motion";
import { NeonOpenSign } from "@/components/NeonOpenSign";
import { EmailSignup } from "@/components/EmailSignup";
import { OfflineIndicator } from "@/components/OfflineIndicator";
const AdminHelpWidget = lazy(() =>
  import("@/components/AdminHelpWidget").then(m => ({ default: m.AdminHelpWidget }))
);
const HomeChatWidget = lazy(() =>
  import("@/components/HomeChatWidget").then(m => ({ default: m.HomeChatWidget }))
);
import ceilingTile from "@/assets/images/ceiling-texture.webp";
import { magikVarsSmall } from "@/lib/magik-styles";
import { useShopStatus } from "@/hooks/useShopStatus";

interface UpcomingEvent {
  id: number;
  title: string;
  description: string;
  date: string;
  time: string;
  location: string;
  category: string;
  image: string | null;
}


export function Layout({ children }: { children: React.ReactNode }) {
  const [location] = useLocation();
  const isAdminPage = location.startsWith("/admin") || location.startsWith("/walkin") || location.startsWith("/artist-portal") || location.startsWith("/receptionist-portal") || location.startsWith("/agency-portal");
  const { isInstalled } = usePWAInstall();
  const { config: studio } = useStudioConfig();
  const isAgency = studio.profile === "agency";
  const { isFlagEnabled, isLoaded: flagsLoaded } = useFeatureFlags();
  // Optimistic nav: show items while flags load to avoid flicker on ICP-1.
  // Guarded routes in App.tsx enforce the actual redirect if a flag is off.
  const showFlash    = !flagsLoaded || isFlagEnabled("flash_gallery_enabled");
  const showEvents   = !flagsLoaded || isFlagEnabled("events_page_enabled");
  const showHealing  = !flagsLoaded || isFlagEnabled("healing_tracker_enabled");
  const showQuiz     = !flagsLoaded || isFlagEnabled("style_quiz_enabled");
  const _showPiercings = flagsLoaded && isFlagEnabled("piercings_enabled");
  const [menuOpen, setMenuOpen] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);
  const [helpBadgeCount, setHelpBadgeCount] = useState(0);
  useEffect(() => {
    if (!menuOpen) return;
    // iOS Safari ignores overflow:hidden on body — position:fixed is the
    // reliable cross-platform scroll lock. We save and restore scrollY so
    // the page doesn't jump to the top when the menu closes.
    const scrollY = window.scrollY;
    document.body.style.position = "fixed";
    document.body.style.top = `-${scrollY}px`;
    document.body.style.width = "100%";
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.position = "";
      document.body.style.top = "";
      document.body.style.width = "";
      document.body.style.overflow = "";
      window.scrollTo(0, scrollY);
    };
  }, [menuOpen]);
  const [artistsExpanded, setArtistsExpanded] = useState(false);
  const [desktopArtistsOpen, setDesktopArtistsOpen] = useState(false);
  const [desktopExploreOpen, setDesktopExploreOpen] = useState(false);
  const [desktopInkOpen, setDesktopInkOpen] = useState(false);
  const [desktopToolsOpen, setDesktopToolsOpen] = useState(false);

  const [showScrollTop, setShowScrollTop] = useState(false);
  const [swUpdateReady, setSwUpdateReady] = useState(false);
  const [emailSubscribed, setEmailSubscribed] = useState(() => {
    try { const s = localStorage.getItem("mm_email_signup"); return s ? JSON.parse(s).subscribed === true : false; } catch { return false; }
  });
  const [emailWidgetOpen, setEmailWidgetOpen] = useState(false);
  const emailTapRef = useRef<number[]>([]);
  const handleFooterTripleTap = useTripleTap(useCallback(() => {
    window.location.href = "/admin/agency/dev-token";
  }, []));

  // ── ? keyboard shortcut — opens the help panel on admin pages ────────────
  useEffect(() => {
    if (!isAdminPage || isAgency) return;
    function handler(e: KeyboardEvent) {
      if (isAgency) return;
      const tag = (e.target as HTMLElement).tagName;
      if (tag === "INPUT" || tag === "TEXTAREA" || (e.target as HTMLElement).isContentEditable) return;
      if (e.key === "?" && !e.ctrlKey && !e.metaKey && !e.altKey) {
        e.preventDefault();
        setHelpOpen(v => !v);
      }
      if (e.key === "Escape") setHelpOpen(false);
    }
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [isAdminPage, isAgency]);

  useEffect(() => {
    const onToggle = () => setEmailWidgetOpen(prev => !prev);
    const onClose = () => setEmailWidgetOpen(false);
    window.addEventListener("open-email-signup", onToggle);
    window.addEventListener("email-signup-closed", onClose);
    return () => {
      window.removeEventListener("open-email-signup", onToggle);
      window.removeEventListener("email-signup-closed", onClose);
    };
  }, []);
  // INC-2026-05-10: listen for the SW update event dispatched by sw-register.ts
  // when a new service worker has taken control. We no longer auto-reload on
  // controllerchange — instead we show a dismissable banner so users can choose
  // when to reload.
  useEffect(() => {
    const onSwUpdate = () => setSwUpdateReady(true);
    window.addEventListener("sw-update-available", onSwUpdate);
    return () => window.removeEventListener("sw-update-available", onSwUpdate);
  }, []);
  useEffect(() => {
    const checkSubscribed = () => {
      try { const s = localStorage.getItem("mm_email_signup"); if (s && JSON.parse(s).subscribed) setEmailSubscribed(true); } catch { /* intentionally silent */ }
    };
    window.addEventListener("storage", checkSubscribed);
    const id = setInterval(checkSubscribed, 2000);
    return () => { window.removeEventListener("storage", checkSubscribed); clearInterval(id); };
  }, []);
  const { config } = useSiteConfig();
  const shopStatus = useShopStatus();
  const artists = getAllArtists();
  const { data: buildInfo } = useQuery<BuildInfo>({
    queryKey: ["/api/build-info"],
    queryFn: () => apiFetch(buildInfoContract),
    staleTime: Infinity,
    refetchOnWindowFocus: false,
  });

  const { data: upcomingEvents } = useQuery<UpcomingEvent[]>({
    queryKey: ["/api/events/upcoming"],
    queryFn: async () => {
      const events = await apiFetch(eventsUpcomingContract);
      return events.map((e) => ({
        id: e.id,
        title: e.title,
        description: e.description ?? "",
        date: e.date ?? "",
        time: e.time ?? "",
        location: e.location ?? "",
        category: e.category ?? "",
        image: e.image ?? null,
      }));
    },
    staleTime: 5 * 60 * 1000,
  });

  const eventDayLabels = useMemo(() => {
    if (!upcomingEvents?.length) return [];
    return upcomingEvents
      .filter((ev) => {
        const d = new Date(ev.date + "T00:00:00");
        const now = new Date();
        const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        const diff = Math.ceil((d.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
        return diff >= 0 && diff <= 30;
      })
      .map((ev) => {
        const d = new Date(ev.date + "T00:00:00");
        const dayName = d.toLocaleDateString("en-US", { weekday: "long" });
        const month = d.toLocaleDateString("en-US", { month: "long" });
        const day = d.getDate();
        return `${dayName}, ${month} ${day}: 10 a.m. to 8 p.m. (${ev.title})`;
      });
  }, [upcomingEvents]);

  const rafRef = useRef(0);
  useEffect(() => {
    const handleScroll = () => {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = requestAnimationFrame(() => {
        setShowScrollTop(window.scrollY > 400);
      });
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", handleScroll);
      cancelAnimationFrame(rafRef.current);
    };
  }, []);

  const footerRef = useRef<HTMLElement>(null);
  const [footerInView, setFooterInView] = useState(false);
  useEffect(() => {
    const el = footerRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        const visible = entry.isIntersecting;
        setFooterInView(visible);
        document.body.classList.toggle("footer-in-view", visible);
      },
      { threshold: 0 }
    );
    observer.observe(el);
    return () => {
      observer.disconnect();
      document.body.classList.remove("footer-in-view");
    };
  }, []);

  const scrollToTop = useCallback(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  const glowStyle = useMemo(
    () => magikVarsSmall(config.magikGlowColor1, config.magikGlowColor2, config.magikGlowIntensity, config.magikGlowSpeed),
    [config.magikGlowColor1, config.magikGlowColor2, config.magikGlowIntensity, config.magikGlowSpeed]
  );

  return (
    <div
      className="min-h-screen text-foreground flex flex-col font-sans selection:bg-accent selection:text-white relative"
      style={(isAdminPage || isAgency) ? {
        backgroundColor: "#080808",
      } : {
        backgroundImage: `url(${studio.backgroundTileUrl || ceilingTile})`,
        backgroundRepeat: "repeat",
        backgroundSize: `${config.tileSize}px ${Math.round(config.tileSize * 1082 / 1107)}px`,
        backgroundPosition: `${config.tileOffsetX}px ${config.tileOffsetY}px`,
        backgroundColor: "hsl(0 0% 5%)",
      }}
    >
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:fixed focus:top-4 focus:left-4 focus:z-[9999] focus:bg-accent focus:text-white focus:px-6 focus:py-3 focus:rounded-lg focus:text-sm focus:uppercase focus:tracking-widest focus:font-subheading focus:shadow-lg"
        data-testid="link-skip-to-content"
      >
        Skip to main content
      </a>
      <header className="fixed top-0 w-full z-50 bg-black border-b border-white/5 overflow-x-clip">
        <div className="container mx-auto px-4 sm:px-6 h-20 flex items-center justify-between">
          <Link
            href="/"
            className="text-lg sm:text-2xl font-heading tracking-wide lg:tracking-widest hover:text-accent transition-colors block uppercase font-semibold whitespace-nowrap shrink-0"
            data-testid="link-home-logo"
          >
            {isAgency ? (
              <img
                src="/brand/cinematic-dark.png"
                alt="Tattoo Bench"
                className="h-14 w-auto object-contain"
                style={{ maxWidth: 300 }}
              />
            ) : studio.name ? (
              <span
                className={`magik-glow-text ${config.italicText ? "italic" : ""}`}
                style={glowStyle}
              >{studio.name}</span>
            ) : (
              <span
                className="inline-block align-middle rounded bg-white/10 animate-pulse"
                style={{ width: 140, height: 22 }}
                aria-hidden="true"
              />
            )}
          </Link>

          <nav aria-label="Main navigation" className="hidden lg:flex gap-4 items-center overflow-visible">
            {isAgency ? (
              /* ── Agency nav ─────────────────────────────────────────────── */
              <>
                <PrefetchLink
                  href="/netglowup/features"
                  data-testid="link-nav-agency-features"
                  aria-current={location.startsWith("/netglowup/features") ? "page" : undefined}
                  className={`text-xs uppercase tracking-[0.2em] font-subheading hover:text-violet-300 transition-colors flex items-center gap-1.5 ${location.startsWith("/netglowup/features") ? "text-violet-300" : "text-muted-foreground"}`}
                >
                  <Sparkles size={13} className="shrink-0" />
                  Features
                </PrefetchLink>
                <PrefetchLink
                  href="/overview"
                  data-testid="link-nav-agency-overview"
                  aria-current={location === "/overview" ? "page" : undefined}
                  className={`text-xs uppercase tracking-[0.2em] font-subheading hover:text-violet-300 transition-colors flex items-center gap-1.5 ${location === "/overview" ? "text-violet-300" : "text-muted-foreground"}`}
                >
                  <Presentation size={13} className="shrink-0" />
                  Overview
                </PrefetchLink>
                <PrefetchLink
                  href="/contact"
                  data-testid="link-nav-contact"
                  aria-current={location === "/contact" ? "page" : undefined}
                  className={`text-xs uppercase tracking-[0.2em] font-subheading hover:text-violet-300 transition-colors flex items-center gap-1.5 ${location === "/contact" ? "text-violet-300" : "text-muted-foreground"}`}
                >
                  <Mail size={13} className="shrink-0" />
                  Contact
                </PrefetchLink>
                <PrefetchLink
                  href="/netglowup/studio-owner/watch?ref=nav"
                  data-testid="link-nav-agency-demo"
                  aria-current={location.startsWith("/netglowup/studio-owner/watch") ? "page" : undefined}
                  className="text-xs uppercase tracking-widest font-subheading font-bold bg-accent text-white px-4 py-2 rounded-full hover:bg-accent/80 transition-all duration-200 flex items-center gap-1.5 shadow-[0_0_12px_rgba(139,92,246,0.35)]"
                >
                  <PlayCircle size={13} className="shrink-0" />
                  See How It Works
                </PrefetchLink>
              </>
            ) : (
              /* ── Studio nav ─────────────────────────────────────────────── */
              <>
                <div
                  className="relative"
                  onMouseEnter={() => setDesktopArtistsOpen(true)}
                  onMouseLeave={() => setDesktopArtistsOpen(false)}
                >
                  <Link
                    href="/#artists"
                    className={`text-xs uppercase tracking-[0.2em] font-subheading hover:text-violet-300 transition-colors flex items-center gap-1.5 ${location.startsWith("/artist") ? "text-violet-300" : "text-muted-foreground"}`}
                  >
                    <Users size={13} className="shrink-0" />
                    Artists{" "}
                    <ChevronDown size={12} className={`transition-transform ${desktopArtistsOpen ? "rotate-180" : ""}`} />
                  </Link>
                  {desktopArtistsOpen && (
                    <div className="absolute top-full left-0 pt-2 min-w-[220px]">
                      <div className="bg-black/95 border border-white/10 rounded-md py-2 shadow-xl">
                        {artists.map((artist) => {
                          const Icon = ARTIST_ICONS[artist.slug] || Palette;
                          return (
                            <PrefetchLink
                              key={artist.slug}
                              href={`/artist/${artist.slug}`}
                              data-testid={`link-nav-artist-${artist.slug}`}
                              className="flex items-center gap-3 px-5 py-3 text-xs uppercase tracking-[0.2em] font-subheading text-white/80 hover:bg-white/5 transition-colors group"
                            >
                              <Icon size={13} className="shrink-0" style={{ color: artist.accentColor }} />
                              <span className="group-hover:text-white transition-colors">{artist.name}</span>
                              <span className="ml-auto w-1.5 h-1.5 rounded-full opacity-60" style={{ backgroundColor: artist.accentColor }} />
                            </PrefetchLink>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
                <div
                  className="relative"
                  onMouseEnter={() => setDesktopExploreOpen(true)}
                  onMouseLeave={() => setDesktopExploreOpen(false)}
                >
                  <button
                    className={`text-xs uppercase tracking-[0.2em] font-subheading hover:text-violet-300 transition-colors flex items-center gap-1.5 ${["/about", "/events", "/gallery"].includes(location) ? "text-violet-300" : "text-muted-foreground"}`}
                    data-testid="link-nav-explore"
                  >
                    <Sparkles size={13} className="shrink-0" />
                    Explore{" "}
                    <ChevronDown size={12} className={`transition-transform ${desktopExploreOpen ? "rotate-180" : ""}`} />
                  </button>
                  {desktopExploreOpen && (
                    <div className="absolute top-full left-0 pt-2 min-w-[200px]">
                      <div className="bg-black/95 border border-white/10 rounded-md py-2 shadow-xl">
                        <PrefetchLink href="/about" data-testid="link-nav-about" className="flex items-center gap-3 px-5 py-3 text-xs uppercase tracking-[0.2em] font-subheading text-white/80 hover:text-accent hover:bg-white/5 transition-colors">
                          <Info size={13} className="shrink-0 opacity-60" />
                          About
                        </PrefetchLink>
                        {showEvents && (
                          <FeatureFlagZone flagKey="events_page_enabled" label="events nav">
                            <PrefetchLink href="/events" data-testid="link-nav-events" className="flex items-center gap-3 px-5 py-3 text-xs uppercase tracking-[0.2em] font-subheading text-white/80 hover:text-accent hover:bg-white/5 transition-colors">
                              <CalendarDays size={13} className="shrink-0 opacity-60" />
                              Events
                            </PrefetchLink>
                          </FeatureFlagZone>
                        )}
                        <PrefetchLink href="/mailing-list" data-testid="link-nav-mailing-list" className="flex items-center gap-3 px-5 py-3 text-xs uppercase tracking-[0.2em] font-subheading text-white/80 hover:text-accent hover:bg-white/5 transition-colors">
                          <Mail size={13} className="shrink-0 opacity-60" />
                          Newsletter
                        </PrefetchLink>
                        <PrefetchLink href="/gallery" data-testid="link-nav-gallery" className="flex items-center gap-3 px-5 py-3 text-xs uppercase tracking-[0.2em] font-subheading text-white/80 hover:text-accent hover:bg-white/5 transition-colors">
                          <ImageIcon size={13} className="shrink-0 opacity-60" />
                          Gallery
                        </PrefetchLink>
                        <PrefetchLink href="/overview" data-testid="link-nav-overview" className="flex items-center gap-3 px-5 py-3 text-xs uppercase tracking-[0.2em] font-subheading text-white/80 hover:text-accent hover:bg-white/5 transition-colors">
                          <Presentation size={13} className="shrink-0 opacity-60" />
                          Platform Overview
                        </PrefetchLink>
                      </div>
                    </div>
                  )}
                </div>
                <div
                  className="relative"
                  onMouseEnter={() => setDesktopInkOpen(true)}
                  onMouseLeave={() => setDesktopInkOpen(false)}
                >
                  <button
                    className={`text-xs uppercase tracking-[0.2em] font-subheading hover:text-violet-300 transition-colors flex items-center gap-1.5 ${["/flash", "/shop"].includes(location) ? "text-violet-300" : "text-muted-foreground"}`}
                    data-testid="link-nav-ink"
                  >
                    <Palette size={13} className="shrink-0" />
                    Ink{" "}
                    <ChevronDown size={12} className={`transition-transform ${desktopInkOpen ? "rotate-180" : ""}`} />
                  </button>
                  {desktopInkOpen && (
                    <div className="absolute top-full left-0 pt-2 min-w-[200px]">
                      <div className="bg-black/95 border border-white/10 rounded-md py-2 shadow-xl">
                        {showFlash && (
                          <FeatureFlagZone flagKey="flash_gallery_enabled" label="flash nav">
                            <PrefetchLink href="/flash" data-testid="link-nav-flash" className="flex items-center gap-3 px-5 py-3 text-xs uppercase tracking-[0.2em] font-subheading text-white/80 hover:text-accent hover:bg-white/5 transition-colors">
                              <Flame size={13} className="shrink-0 opacity-60" />
                              Flash Designs
                            </PrefetchLink>
                          </FeatureFlagZone>
                        )}
                        <PrefetchLink href="/shop" data-testid="link-nav-shop" className="flex items-center gap-3 px-5 py-3 text-xs uppercase tracking-[0.2em] font-subheading text-white/80 hover:text-accent hover:bg-white/5 transition-colors">
                          <ShoppingBag size={13} className="shrink-0 opacity-60" />
                          Shop
                        </PrefetchLink>
                      </div>
                    </div>
                  )}
                </div>
                <div
                  className="relative"
                  onMouseEnter={() => setDesktopToolsOpen(true)}
                  onMouseLeave={() => setDesktopToolsOpen(false)}
                >
                  <button
                    className={`text-xs uppercase tracking-[0.2em] font-subheading hover:text-violet-300 transition-colors flex items-center gap-1.5 ${["/healing", "/quiz", "/estimate", "/app"].includes(location) ? "text-violet-300" : "text-muted-foreground"}`}
                    data-testid="link-nav-tools"
                  >
                    <Activity size={13} className="shrink-0" />
                    Tools{" "}
                    <ChevronDown size={12} className={`transition-transform ${desktopToolsOpen ? "rotate-180" : ""}`} />
                  </button>
                  {desktopToolsOpen && (
                    <div className="absolute top-full left-0 pt-2 min-w-[200px]">
                      <div className="bg-black/95 border border-white/10 rounded-md py-2 shadow-xl">
                        {showHealing && (
                          <FeatureFlagZone flagKey="healing_tracker_enabled" label="healing nav">
                            <PrefetchLink href="/healing" data-testid="link-nav-healing" className="flex items-center gap-3 px-5 py-3 text-xs uppercase tracking-[0.2em] font-subheading text-white/80 hover:text-accent hover:bg-white/5 transition-colors">
                              <Heart size={13} className="shrink-0 opacity-60" />
                              Healing Tracker
                            </PrefetchLink>
                          </FeatureFlagZone>
                        )}
                        {showQuiz && (
                          <FeatureFlagZone flagKey="style_quiz_enabled" label="quiz nav">
                            <PrefetchLink href="/quiz" data-testid="link-nav-quiz" className="flex items-center gap-3 px-5 py-3 text-xs uppercase tracking-[0.2em] font-subheading text-white/80 hover:text-accent hover:bg-white/5 transition-colors">
                              <Sparkles size={13} className="shrink-0 opacity-60" />
                              Style Quiz
                            </PrefetchLink>
                          </FeatureFlagZone>
                        )}
                        <PrefetchLink href="/estimate" data-testid="link-nav-pricing" className="flex items-center gap-3 px-5 py-3 text-xs uppercase tracking-[0.2em] font-subheading text-white/80 hover:text-accent hover:bg-white/5 transition-colors">
                          <DollarSign size={13} className="shrink-0 opacity-60" />
                          Pricing
                        </PrefetchLink>
                        <PrefetchLink href="/app" data-testid="link-nav-app" className="flex items-center gap-3 px-5 py-3 text-xs uppercase tracking-[0.2em] font-subheading text-white/80 hover:text-accent hover:bg-white/5 transition-colors">
                          <Smartphone size={13} className="shrink-0 opacity-60" />
                          Get the App
                        </PrefetchLink>
                      </div>
                    </div>
                  )}
                </div>
                {!isAdminPage && (
                  <PrefetchLink
                    href="/login"
                    data-testid="link-nav-login"
                    aria-current={location === "/login" ? "page" : undefined}
                    className={`text-xs uppercase tracking-[0.2em] font-subheading hover:text-violet-300 transition-colors flex items-center gap-1.5 ${location === "/login" ? "text-violet-300" : "text-muted-foreground"}`}
                  >
                    <LogIn size={13} className="shrink-0" />
                    Login
                  </PrefetchLink>
                )}
                <PrefetchLink
                  href="/booking"
                  data-testid="link-nav-booking"
                  aria-current={location === "/booking" ? "page" : undefined}
                  className={`text-xs uppercase tracking-[0.2em] font-subheading hover:text-violet-300 transition-colors flex items-center gap-1.5 ${location === "/booking" ? "text-violet-300" : "text-muted-foreground"}`}
                >
                  <CalendarDays size={13} className="shrink-0" />
                  Book
                </PrefetchLink>
                <PrefetchLink
                  href="/contact"
                  data-testid="link-nav-contact"
                  aria-current={location === "/contact" ? "page" : undefined}
                  className={`text-xs uppercase tracking-[0.2em] font-subheading hover:text-violet-300 transition-colors flex items-center gap-1.5 ${location === "/contact" ? "text-violet-300" : "text-muted-foreground"}`}
                >
                  <Mail size={13} className="shrink-0" />
                  Contact
                </PrefetchLink>
              </>
            )}
          </nav>

          <div className="flex items-center gap-2">
            {isAdminPage && <Suspense fallback={null}><AdminCommandPaletteButton /></Suspense>}
            {isAdminPage && <Suspense fallback={null}><GlobalAdminSearch /></Suspense>}
            {/* Help button — opens the report-an-issue panel; ? keyboard shortcut also works */}
            {isAdminPage && !isAgency && !location.startsWith("/artist-portal") && !location.startsWith("/receptionist-portal") && !location.startsWith("/agency-portal") && (
              <button
                data-help-trigger="true"
                data-testid="button-help-toggle"
                onClick={() => setHelpOpen(v => !v)}
                aria-label={helpOpen ? "Close help panel" : "Help / report an issue (?)"}
                aria-expanded={helpOpen}
                style={{
                  position: "relative",
                  height: 32,
                  width: 32,
                  borderRadius: "50%",
                  background: helpOpen ? "#1e1030" : "transparent",
                  border: `1px solid ${helpOpen ? "#7c3aedcc" : "rgba(255,255,255,0.12)"}`,
                  color: helpOpen ? "#a78bfa" : "rgba(255,255,255,0.55)",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  transition: "background 0.15s, border-color 0.15s, color 0.15s",
                  flexShrink: 0,
                }}
                onMouseEnter={e => { if (!helpOpen) { (e.currentTarget as HTMLButtonElement).style.borderColor = "rgba(139,92,246,0.5)"; (e.currentTarget as HTMLButtonElement).style.color = "#a78bfa"; } }}
                onMouseLeave={e => { if (!helpOpen) { (e.currentTarget as HTMLButtonElement).style.borderColor = "rgba(255,255,255,0.12)"; (e.currentTarget as HTMLButtonElement).style.color = "rgba(255,255,255,0.55)"; } }}
              >
                <HelpCircle size={15} />
                {helpBadgeCount > 0 && !helpOpen && (
                  <div style={{ position: "absolute", top: -4, right: -4, minWidth: 16, height: 16, borderRadius: 8, background: "#2dd4bf", color: "#000", fontSize: 9, fontWeight: 800, display: "flex", alignItems: "center", justifyContent: "center", padding: "0 3px", border: "1.5px solid #111", lineHeight: 1, pointerEvents: "none" }}>
                    {helpBadgeCount > 9 ? "9+" : helpBadgeCount}
                  </div>
                )}
              </button>
            )}
            {/* Hamburger button */}
            <button
              onClick={() => setMenuOpen(true)}
              className={`lg:hidden inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-xs uppercase tracking-widest font-subheading font-bold border transition-all active:scale-[0.97] ${
                menuOpen
                  ? "bg-accent border-accent text-white shadow-[0_0_12px_rgba(139,92,246,0.4)]"
                  : "bg-white/5 border-white/15 text-white hover:bg-accent/20 hover:border-accent/40 hover:text-accent"
              }`}
              data-testid="button-menu-hamburger"
              aria-label="Open menu"
              aria-expanded={menuOpen}
              aria-controls="mobile-nav-panel"
            >
              <Menu size={13} className="shrink-0" />
              Menu
            </button>

            {/* Backdrop — always in DOM, fades in/out */}
            <div
              className="fixed inset-0 z-40 bg-black/80 lg:hidden transition-opacity duration-300"
              style={{ opacity: menuOpen ? 1 : 0, pointerEvents: menuOpen ? "auto" : "none" }}
              onClick={() => setMenuOpen(false)}
              aria-hidden="true"
            />

            {/* Slide panel — always in DOM, no mount cost */}
            <div
              id="mobile-nav-panel"
              role="dialog"
              aria-modal="true"
              aria-label="Navigation menu"
              aria-hidden={!menuOpen}
              className="fixed top-0 right-0 h-full w-72 z-50 bg-black/95 border-l border-white/10 overflow-y-auto will-change-transform transition-transform duration-300 ease-in-out lg:hidden"
              style={{ transform: menuOpen ? "translateX(0)" : "translateX(100%)" }}
            >
              {isAgency ? (
                /* ── Agency mobile panel header ─────────────────────────── */
                <div className="px-4 pt-5 pb-4 border-b border-white/10 text-center">
                  <h4 className="font-heading text-xl tracking-wide mb-1">
                    <span className="magik-glow-text italic" style={{ "--magik-color1": "hsl(265 80% 65%)", "--magik-color2": "hsl(280 90% 70%)" } as React.CSSProperties}>
                      Tattoo Bench
                    </span>
                  </h4>
                  <p className="text-[10px] text-muted-foreground uppercase tracking-[0.2em] font-subheading mb-4">
                    Studio management platform
                  </p>
                </div>
              ) : (
                /* ── Studio mobile panel header ──────────────────────────── */
                <div className="px-4 pt-5 pb-4 border-b border-white/10 text-center">
                  <h4 className="font-heading text-xl tracking-wide mb-1">
                    <span className="magik-glow-text italic" style={{ "--magik-color1": "hsl(265 80% 65%)", "--magik-color2": "hsl(280 90% 70%)" } as React.CSSProperties}>
                      {studio.shortName}
                    </span>{" "}
                    <span className="text-white">{studio.brandSuffix}</span>
                  </h4>
                  <p className="text-[10px] text-muted-foreground uppercase tracking-[0.2em] font-subheading mb-4">
                    {studio.slogan} &middot; Est. {studio.established}
                  </p>
                  <div className="flex justify-center gap-3 mb-4">
                    <a
                      href={studio.instagramUrl || "#"}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label="Follow us on Instagram"
                      className="w-9 h-9 rounded-full flex items-center justify-center text-white hover:scale-110 transition-all"
                      style={{ background: "linear-gradient(45deg, #f09433, #e6683c, #dc2743, #cc2366, #bc1888)" }}
                    >
                      <Instagram size={16} />
                    </a>
                    <a
                      href={studio.facebookUrl || "#"}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label="Follow us on Facebook"
                      className="w-9 h-9 rounded-full flex items-center justify-center text-white hover:scale-110 transition-all"
                      style={{ backgroundColor: "#1877F2" }}
                    >
                      <Facebook size={16} />
                    </a>
                    <a
                      href={`tel:${studio.phone}`}
                      aria-label={`Call us at ${studio.phone}`}
                      className="w-9 h-9 rounded-full flex items-center justify-center text-white hover:scale-110 transition-all"
                      style={{ backgroundColor: "#22c55e" }}
                    >
                      <Phone size={16} />
                    </a>
                    <a
                      href="/contact"
                      aria-label="Contact us"
                      className="w-9 h-9 rounded-full flex items-center justify-center text-white hover:scale-110 transition-all"
                      style={{ backgroundColor: "#7c3aed" }}
                    >
                      <Mail size={16} />
                    </a>
                  </div>
                </div>
              )}
              <nav aria-label="Mobile navigation" className="flex flex-col pt-3 px-4 gap-1.5 pb-6">
                {isAgency ? (
                  /* ── Agency mobile nav items ──────────────────────────── */
                  <>
                    <PrefetchLink
                      href="/"
                      onClick={() => setMenuOpen(false)}
                      aria-current={location === "/" ? "page" : undefined}
                      className={`py-2.5 px-3 text-sm uppercase tracking-[0.2em] font-subheading hover:text-accent active:text-accent active:scale-95 transition-all rounded-lg border flex items-center gap-3 ${location === "/" ? "text-white bg-accent border-accent font-bold" : "text-white border-white/10 bg-white/5"}`}
                    >
                      <Home size={15} className="text-violet-400" />
                      Home
                    </PrefetchLink>
                    <PrefetchLink
                      href="/netglowup"
                      onClick={() => setMenuOpen(false)}
                      data-testid="link-mobile-agency-features"
                      aria-current={location === "/netglowup" ? "page" : undefined}
                      className={`py-2.5 px-3 text-sm uppercase tracking-[0.2em] font-subheading hover:text-accent active:text-accent active:scale-95 transition-all rounded-lg border flex items-center gap-3 ${location === "/netglowup" ? "text-white bg-accent border-accent font-bold" : "text-white border-white/10 bg-white/5"}`}
                    >
                      <Sparkles size={15} className="text-violet-400" />
                      Features
                    </PrefetchLink>
                    <PrefetchLink
                      href="/contact"
                      onClick={() => setMenuOpen(false)}
                      data-testid="link-mobile-contact"
                      aria-current={location === "/contact" ? "page" : undefined}
                      className={`py-2.5 px-3 text-sm uppercase tracking-[0.2em] font-subheading hover:text-accent active:text-accent active:scale-95 transition-all rounded-lg border flex items-center gap-3 ${location === "/contact" ? "text-white bg-accent border-accent font-bold" : "text-white border-white/10 bg-white/5"}`}
                    >
                      <Mail size={15} className="text-cyan-400" />
                      Contact
                    </PrefetchLink>
                    <PrefetchLink
                      href="/overview"
                      onClick={() => setMenuOpen(false)}
                      data-testid="link-mobile-agency-overview"
                      aria-current={location === "/overview" ? "page" : undefined}
                      className={`py-2.5 px-3 text-sm uppercase tracking-[0.2em] font-subheading hover:text-accent active:text-accent active:scale-95 transition-all rounded-lg border flex items-center gap-3 ${location === "/overview" ? "text-white bg-accent border-accent font-bold" : "text-white border-white/10 bg-white/5"}`}
                    >
                      <Presentation size={15} className="text-amber-400" />
                      Overview
                    </PrefetchLink>
                    <PrefetchLink
                      href="/netglowup/studio-owner/watch?ref=nav-mobile"
                      onClick={() => setMenuOpen(false)}
                      data-testid="link-mobile-agency-demo"
                      aria-current={location.startsWith("/netglowup/studio-owner/watch") ? "page" : undefined}
                      className="py-2.5 px-3 text-sm uppercase tracking-[0.2em] font-subheading bg-accent text-white rounded-lg border border-accent flex items-center gap-3 font-bold shadow-[0_0_10px_rgba(139,92,246,0.3)]"
                    >
                      <PlayCircle size={15} />
                      See How It Works
                    </PrefetchLink>
                  </>
                ) : (
                  /* ── Studio mobile nav items ──────────────────────────── */
                  <>
                {!isAdminPage && (
                  <PrefetchLink
                    href="/login"
                    onClick={() => setMenuOpen(false)}
                    aria-current={location === "/login" ? "page" : undefined}
                    data-testid="link-mobile-login"
                    className={`py-2.5 px-3 text-sm uppercase tracking-[0.2em] font-subheading hover:text-accent active:text-accent active:scale-95 transition-all rounded-lg border flex items-center gap-3 ${location === "/login" ? "text-white bg-accent border-accent font-bold" : "text-white border-white/10 bg-white/5"}`}
                  >
                    <LogIn size={15} className="text-violet-300" />
                    Login
                  </PrefetchLink>
                )}
                <PrefetchLink
                  href="/"
                  onClick={() => setMenuOpen(false)}
                  aria-current={location === "/" ? "page" : undefined}
                  className={`py-2.5 px-3 text-sm uppercase tracking-[0.2em] font-subheading hover:text-accent active:text-accent active:scale-95 transition-all rounded-lg border flex items-center gap-3 ${location === "/" ? "text-white bg-accent border-accent font-bold" : "text-white border-white/10 bg-white/5"}`}
                >
                  <Home size={15} className="text-violet-400" />
                  Studio
                </PrefetchLink>
                <div>
                  <button
                    onClick={() => setArtistsExpanded(!artistsExpanded)}
                    className={`w-full py-2.5 px-3 text-sm uppercase tracking-[0.2em] font-subheading hover:text-accent transition-colors rounded-lg border flex items-center justify-between ${artistsExpanded ? "text-white bg-accent border-accent font-bold" : "text-white border-white/10 bg-white/5"}`}
                  >
                    <span className="flex items-center gap-3">
                      <Users size={15} className="text-pink-400" />
                      Artists
                    </span>
                    <ChevronDown
                      size={14}
                      className={`transition-transform ${artistsExpanded ? "rotate-180" : ""}`}
                    />
                  </button>
                  {artistsExpanded && (
                    <div className="pl-3 mt-1 space-y-1">
                      {artists.map((artist) => {
                        const Icon = ARTIST_ICONS[artist.slug] || Palette;
                        const isActive = location === `/artist/${artist.slug}`;
                        return (
                          <PrefetchLink
                            key={artist.slug}
                            href={`/artist/${artist.slug}`}
                            onClick={() => setMenuOpen(false)}
                            className={`flex items-center gap-3 py-2 px-3 text-xs uppercase tracking-[0.2em] font-subheading transition-all rounded-md border ${isActive ? "text-white font-bold border-white/20 bg-white/10" : "text-white/70 hover:text-white border-white/5 bg-white/[0.03] hover:border-white/15 hover:bg-white/[0.06]"}`}
                            style={{ borderLeftWidth: 3, borderLeftColor: artist.accentColor }}
                          >
                            <Icon size={14} className="shrink-0" style={{ color: artist.accentColor }} />
                            <span className="flex-1">{artist.name}</span>
                            <span className="text-[9px] tracking-normal normal-case font-normal opacity-50">{artist.specialties[0]}</span>
                          </PrefetchLink>
                        );
                      })}
                    </div>
                  )}
                </div>
                <PrefetchLink
                  href="/about"
                  onClick={() => setMenuOpen(false)}
                  data-testid="link-mobile-about"
                  aria-current={location === "/about" ? "page" : undefined}
                  className={`py-2.5 px-3 text-sm uppercase tracking-[0.2em] font-subheading hover:text-accent active:text-accent active:scale-95 transition-all rounded-lg border flex items-center gap-3 ${location === "/about" ? "text-white bg-accent border-accent font-bold" : "text-white border-white/10 bg-white/5"}`}
                >
                  <Info size={15} className="text-sky-400" />
                  About
                </PrefetchLink>
                {showEvents && (
                  <FeatureFlagZone flagKey="events_page_enabled" label="events nav">
                    <PrefetchLink
                      href="/events"
                      onClick={() => setMenuOpen(false)}
                      data-testid="link-mobile-events"
                      aria-current={location === "/events" ? "page" : undefined}
                      className={`py-2.5 px-3 text-sm uppercase tracking-[0.2em] font-subheading hover:text-accent active:text-accent active:scale-95 transition-all rounded-lg border flex items-center gap-3 ${location === "/events" ? "text-white bg-accent border-accent font-bold" : "text-white border-white/10 bg-white/5"}`}
                    >
                      <CalendarDays size={15} className="text-orange-400" />
                      Events
                    </PrefetchLink>
                  </FeatureFlagZone>
                )}
                <PrefetchLink
                  href="/mailing-list"
                  onClick={() => setMenuOpen(false)}
                  data-testid="link-mobile-mailing-list"
                  aria-current={location === "/mailing-list" ? "page" : undefined}
                  className={`py-2.5 px-3 text-sm uppercase tracking-[0.2em] font-subheading hover:text-accent active:text-accent active:scale-95 transition-all rounded-lg border flex items-center gap-3 ${location === "/mailing-list" ? "text-white bg-accent border-accent font-bold" : "text-white border-white/10 bg-white/5"}`}
                >
                  <Mail size={15} className="text-purple-400" />
                  Newsletter
                </PrefetchLink>
                <PrefetchLink
                  href="/gallery"
                  onClick={() => setMenuOpen(false)}
                  data-testid="link-mobile-gallery"
                  aria-current={location === "/gallery" ? "page" : undefined}
                  className={`py-2.5 px-3 text-sm uppercase tracking-[0.2em] font-subheading hover:text-accent active:text-accent active:scale-95 transition-all rounded-lg border flex items-center gap-3 ${location === "/gallery" ? "text-white bg-accent border-accent font-bold" : "text-white border-white/10 bg-white/5"}`}
                >
                  <ImageIcon size={15} className="text-teal-400" />
                  Gallery
                </PrefetchLink>
                <PrefetchLink
                  href="/overview"
                  onClick={() => setMenuOpen(false)}
                  data-testid="link-mobile-overview"
                  aria-current={location === "/overview" ? "page" : undefined}
                  className={`py-2.5 px-3 text-sm uppercase tracking-[0.2em] font-subheading hover:text-accent active:text-accent active:scale-95 transition-all rounded-lg border flex items-center gap-3 ${location === "/overview" ? "text-white bg-accent border-accent font-bold" : "text-white border-white/10 bg-white/5"}`}
                >
                  <Presentation size={15} className="text-amber-400" />
                  Platform Overview
                </PrefetchLink>
                {showFlash && (
                  <FeatureFlagZone flagKey="flash_gallery_enabled" label="flash nav">
                    <PrefetchLink
                      href="/flash"
                      onClick={() => setMenuOpen(false)}
                      data-testid="link-mobile-flash"
                      aria-current={location === "/flash" ? "page" : undefined}
                      className={`py-2.5 px-3 text-sm uppercase tracking-[0.2em] font-subheading hover:text-accent active:text-accent active:scale-95 transition-all rounded-lg border flex items-center gap-3 ${location === "/flash" ? "text-white bg-accent border-accent font-bold" : "text-white border-white/10 bg-white/5"}`}
                    >
                      <Sparkles size={15} className="text-yellow-400" />
                      Flashes
                    </PrefetchLink>
                  </FeatureFlagZone>
                )}
                <PrefetchLink
                  href="/shop"
                  onClick={() => setMenuOpen(false)}
                  data-testid="link-mobile-shop"
                  aria-current={location === "/shop" ? "page" : undefined}
                  className={`py-2.5 px-3 text-sm uppercase tracking-[0.2em] font-subheading hover:text-accent active:text-accent active:scale-95 transition-all rounded-lg border flex items-center gap-3 ${location === "/shop" ? "text-white bg-accent border-accent font-bold" : "text-white border-white/10 bg-white/5"}`}
                >
                  <ShoppingBag size={15} className="text-emerald-400" />
                  Shop
                </PrefetchLink>
                {showHealing && (
                  <FeatureFlagZone flagKey="healing_tracker_enabled" label="healing nav">
                    <PrefetchLink
                      href="/healing"
                      onClick={() => setMenuOpen(false)}
                      data-testid="link-mobile-healing"
                      aria-current={location === "/healing" ? "page" : undefined}
                      className={`py-2.5 px-3 text-sm uppercase tracking-[0.2em] font-subheading hover:text-accent active:text-accent active:scale-95 transition-all rounded-lg border flex items-center gap-3 ${location === "/healing" ? "text-white bg-accent border-accent font-bold" : "text-white border-white/10 bg-white/5"}`}
                    >
                      <Activity size={15} className="text-red-400" />
                      Healing
                    </PrefetchLink>
                  </FeatureFlagZone>
                )}
                {showQuiz && (
                  <FeatureFlagZone flagKey="style_quiz_enabled" label="quiz nav">
                    <PrefetchLink
                      href="/quiz"
                      onClick={() => setMenuOpen(false)}
                      data-testid="link-mobile-quiz"
                      aria-current={location === "/quiz" ? "page" : undefined}
                      className={`py-2.5 px-3 text-sm uppercase tracking-[0.2em] font-subheading hover:text-accent active:text-accent active:scale-95 transition-all rounded-lg border flex items-center gap-3 ${location === "/quiz" ? "text-white bg-accent border-accent font-bold" : "text-white border-white/10 bg-white/5"}`}
                    >
                      <Palette size={15} className="text-fuchsia-400" />
                      Style Quiz
                    </PrefetchLink>
                  </FeatureFlagZone>
                )}
                <PrefetchLink
                  href="/estimate"
                  onClick={() => setMenuOpen(false)}
                  data-testid="link-mobile-pricing"
                  aria-current={location === "/estimate" ? "page" : undefined}
                  className={`py-2.5 px-3 text-sm uppercase tracking-[0.2em] font-subheading hover:text-accent active:text-accent active:scale-95 transition-all rounded-lg border flex items-center gap-3 ${location === "/estimate" ? "text-white bg-accent border-accent font-bold" : "text-white border-white/10 bg-white/5"}`}
                >
                  <DollarSign size={15} className="text-green-400" />
                  Pricing
                </PrefetchLink>
                <PrefetchLink
                  href="/booking"
                  onClick={() => setMenuOpen(false)}
                  data-testid="link-mobile-booking"
                  aria-current={location === "/booking" ? "page" : undefined}
                  className={`py-2.5 px-3 text-sm uppercase tracking-[0.2em] font-subheading hover:text-accent active:text-accent active:scale-95 transition-all rounded-lg border flex items-center gap-3 ${location === "/booking" ? "text-white bg-accent border-accent font-bold" : "text-white border-white/10 bg-white/5"}`}
                >
                  <CalendarDays size={15} className="text-violet-400" />
                  Book Now
                </PrefetchLink>
                <PrefetchLink
                  href="/contact"
                  onClick={() => setMenuOpen(false)}
                  data-testid="link-mobile-contact"
                  aria-current={location === "/contact" ? "page" : undefined}
                  className={`py-2.5 px-3 text-sm uppercase tracking-[0.2em] font-subheading hover:text-accent active:text-accent active:scale-95 transition-all rounded-lg border flex items-center gap-3 ${location === "/contact" ? "text-white bg-accent border-accent font-bold" : "text-white border-white/10 bg-white/5"}`}
                >
                  <Mail size={15} className="text-cyan-400" />
                  Contact
                </PrefetchLink>
                <PrefetchLink
                  href="/app"
                  onClick={() => setMenuOpen(false)}
                  data-testid="link-mobile-app"
                  aria-current={location === "/app" ? "page" : undefined}
                  className={`py-2.5 px-3 text-sm uppercase tracking-[0.2em] font-subheading hover:text-accent active:text-accent active:scale-95 transition-all rounded-lg border flex items-center gap-3 ${location === "/app" ? "text-white bg-accent border-accent font-bold" : "text-white border-white/10 bg-white/5"}`}
                >
                  <Smartphone size={15} className="text-indigo-400" />
                  Get the App
                </PrefetchLink>
                  </>
                )}
              </nav>
            </div>
          </div>
        </div>
      </header>

      <main id="main-content" role="main" tabIndex={-1} className="flex-grow relative z-10 pt-20">{children}</main>

      <footer ref={footerRef} role="contentinfo" className="border-t border-white/10 pt-16 pb-8 relative z-10" style={{ backgroundColor: `rgba(0,0,0,${config.textBgOpacity / 100})` }}>
        {isAgency ? (
          /* ── Agency footer ───────────────────────────────────────────── */
          <>
            <div className="container mx-auto px-6">
              <div className="flex flex-col md:flex-row md:items-start gap-12 md:gap-24">
                <div className="max-w-xs">
                  <img
                    src="/brand/cinematic-dark.png"
                    alt="Tattoo Bench"
                    className="h-24 w-auto object-contain mb-4"
                    style={{ maxWidth: 380 }}
                  />
                  <p className="text-muted-foreground leading-relaxed font-light text-sm">
                    {studio.description}
                  </p>
                </div>
                <div>
                  <h4 className="font-subheading text-sm uppercase tracking-widest text-white font-semibold mb-4">
                    Product
                  </h4>
                  <ul className="space-y-3">
                    <li>
                      <PrefetchLink
                        href="/netglowup"
                        data-testid="link-agency-footer-features"
                        className="flex items-center gap-2 text-muted-foreground hover:text-accent transition-colors text-sm font-subheading uppercase tracking-widest"
                      >
                        <Sparkles size={13} className="shrink-0" />
                        Features
                      </PrefetchLink>
                    </li>
                    <li>
                      <PrefetchLink
                        href="/contact"
                        data-testid="link-agency-footer-contact"
                        className="flex items-center gap-2 text-muted-foreground hover:text-accent transition-colors text-sm font-subheading uppercase tracking-widest"
                      >
                        <Mail size={13} className="shrink-0" />
                        Contact
                      </PrefetchLink>
                    </li>
                    <li>
                      <PrefetchLink
                        href="/netglowup/studio-owner/watch?ref=footer"
                        data-testid="link-agency-footer-demo"
                        className="flex items-center gap-2 text-muted-foreground hover:text-accent transition-colors text-sm font-subheading uppercase tracking-widest"
                      >
                        <PlayCircle size={13} className="shrink-0" />
                        See How It Works
                      </PrefetchLink>
                    </li>
                  </ul>
                </div>
                <div>
                  <h4 className="font-subheading text-sm uppercase tracking-widest text-white font-semibold mb-4">
                    Legal
                  </h4>
                  <ul className="space-y-3">
                    <li>
                      <Link
                        href="/privacy"
                        data-testid="link-agency-footer-privacy"
                        className="text-muted-foreground hover:text-accent transition-colors text-sm font-subheading uppercase tracking-widest"
                      >
                        Privacy Policy
                      </Link>
                    </li>
                    <li>
                      <Link
                        href="/terms"
                        data-testid="link-agency-footer-terms"
                        className="text-muted-foreground hover:text-accent transition-colors text-sm font-subheading uppercase tracking-widest"
                      >
                        Terms of Service
                      </Link>
                    </li>
                    <li>
                      <Link
                        href="/accessibility"
                        data-testid="link-agency-footer-accessibility"
                        className="text-muted-foreground hover:text-accent transition-colors text-sm font-subheading uppercase tracking-widest"
                      >
                        Accessibility
                      </Link>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
            <div className="container mx-auto px-6 mt-12 pt-8 border-t border-white/5">
              <div className="flex flex-col items-center gap-2 text-center">
                <p className="text-muted-foreground uppercase tracking-widest font-subheading text-xs">
                  &copy; {new Date().getFullYear()} {studio.name}. All rights reserved.
                </p>
                {buildInfo && (
                  <Link href="/status" data-testid="link-footer-status" className="text-[10px] text-white/50 font-mono tracking-wider hover:text-accent transition-colors block">
                    v{buildInfo.version} &middot; Build {buildInfo.buildId} &middot; {new Date(buildInfo.date).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric", hour: "2-digit", minute: "2-digit" })}
                  </Link>
                )}
              </div>
            </div>
          </>
        ) : (
          /* ── Studio footer ───────────────────────────────────────────── */
          <>
            <div className="container mx-auto px-6 grid md:grid-cols-3 gap-12">
              <div>
                <h3 className="font-heading text-2xl mb-6">
                  <span
                    className="magik-glow-text italic"
                    style={glowStyle}
                  >{studio.shortName}</span>{" "}
                  <span className="text-white italic">{studio.brandSuffix}</span>
                </h3>
                <p className="text-muted-foreground leading-relaxed max-w-sm font-light">
                  {studio.description}
                </p>
              </div>
              <div>
                <div className="mb-6">
                  <h4 className="font-subheading text-sm uppercase tracking-widest text-white font-semibold mb-3">
                    The Studio
                  </h4>
                  <NeonOpenSign />
                </div>
                <div className="space-y-5">
                  <div className="flex items-start gap-4 text-muted-foreground">
                    <MapPin className="text-accent shrink-0 mt-1" size={18} />
                    <div>
                      <h3 className="text-white uppercase tracking-wider text-xs mb-1 font-bold font-subheading">Location</h3>
                      <p className="font-light text-sm">
                        <a href={studio.mapUrl || "#"} target="_blank" rel="noopener noreferrer" className="hover:text-accent transition-colors">
                          {studio.fullAddress}
                        </a>
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4 text-muted-foreground">
                    <Clock className="text-accent shrink-0 mt-1" size={18} />
                    <div>
                      <h3 className="text-white uppercase tracking-wider text-xs mb-1 font-bold font-subheading">Hours</h3>
                      <p className="font-light text-sm">{studio.hours}</p>
                      {eventDayLabels.length > 0 && (
                        <div className="mt-1.5 space-y-0.5">
                          {eventDayLabels.map((label) => (
                            <p key={label} className="text-xs text-pink-400 font-medium">
                              {label}
                            </p>
                          ))}
                        </div>
                      )}
                      {studio.hoursNote && <p className="text-xs text-white/50 mt-1">{studio.hoursNote}</p>}
                      <p className={`text-xs mt-1.5 font-medium ${shopStatus.isOpen ? "text-green-400" : "text-red-400/80"}`} data-testid="text-shop-status">
                        <span className={`inline-block w-1.5 h-1.5 rounded-full mr-1.5 ${shopStatus.isOpen ? "bg-green-400 animate-pulse" : "bg-red-400/80"}`} />
                        {shopStatus.label}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4 text-muted-foreground">
                    <Mail className="text-accent shrink-0 mt-1" size={18} />
                    <div>
                      <h3 className="text-white uppercase tracking-wider text-xs mb-1 font-bold font-subheading">Email</h3>
                      <p className="font-light text-sm">
                        <a href="/contact" className="hover:text-accent transition-colors">Contact us</a>
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4 text-muted-foreground">
                    <Phone className="text-accent shrink-0 mt-1" size={18} />
                    <div>
                      <h3 className="text-white uppercase tracking-wider text-xs mb-1 font-bold font-subheading">Phone</h3>
                      <p className="font-light text-sm">
                        <a href={`tel:${studio.phone}`} className="hover:text-accent transition-colors">{studio.phone}</a>
                      </p>
                    </div>
                  </div>
                </div>
              </div>
              <div>
                <div className="flex items-center gap-4 mb-8">
                <h4 className="font-subheading text-sm uppercase tracking-widest text-white font-semibold whitespace-nowrap">
                  Follow Us
                </h4>
                  <a
                    href={studio.instagramUrl || "#"}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="Follow us on Instagram"
                    className="w-10 h-10 rounded-full flex items-center justify-center text-white hover:scale-110 transition-all"
                    style={{ background: "linear-gradient(45deg, #f09433, #e6683c, #dc2743, #cc2366, #bc1888)" }}
                  >
                    <Instagram size={18} />
                  </a>
                  <a
                    href={studio.facebookUrl || "#"}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="Follow us on Facebook"
                    className="w-10 h-10 rounded-full flex items-center justify-center text-white hover:scale-110 transition-all"
                    style={{ backgroundColor: "#1877F2" }}
                  >
                    <Facebook size={18} />
                  </a>
                </div>

                {!isInstalled && (
                  <Link
                    href="/app"
                    className="flex items-center justify-center gap-3 w-full bg-accent hover:bg-accent/80 text-white rounded-full px-8 py-4 text-sm font-subheading uppercase tracking-[0.2em] font-bold transition-all duration-300 shadow-[0_0_20px_rgba(139,92,246,0.3)]"
                    data-testid="link-footer-app"
                  >
                    <Smartphone size={18} />
                    Get the App
                  </Link>
                )}
              </div>
            </div>

            <div className="w-full mt-10 bg-black py-6 text-center" onClick={handleFooterTripleTap}>
              <h3 className="font-heading text-3xl md:text-4xl tracking-wide mb-2">
                <span className="magik-glow-text italic" style={{ "--magik-color1": "hsl(265 80% 65%)", "--magik-color2": "hsl(280 90% 70%)" } as React.CSSProperties}>
                  {studio.shortName}
                </span>{" "}
                <span className="text-white">{studio.brandSuffix}</span>
              </h3>
              <p className="text-muted-foreground text-xs uppercase tracking-[0.25em] font-subheading">
                {studio.slogan} &middot; Est. {studio.established}
              </p>
            </div>
            {studio.footerImageUrl && (() => {
              const adj = studio.footerImageAdjustments ?? {};
              const posX = adj.positionX ?? 50;
              const posY = adj.positionY ?? 50;
              const brightness = adj.brightness ?? 100;
              const contrast = adj.contrast ?? 100;
              const opacity = adj.opacity ?? 100;
              const rotate = adj.rotate ?? 0;
              const zoom = adj.zoom ?? 100;
              const hasCustomPosition = posX !== 50 || posY !== 50;
              const hasFilter = brightness !== 100 || contrast !== 100;
              const hasTransform = rotate !== 0 || zoom !== 100;
              const hasOpacity = opacity !== 100;
              const imgStyle = (hasCustomPosition || hasFilter || hasTransform || hasOpacity) ? {
                ...(hasCustomPosition ? { objectPosition: `${posX}% ${posY}%` } : {}),
                ...(hasFilter ? { filter: `brightness(${brightness / 100}) contrast(${contrast / 100})` } : {}),
                ...(hasOpacity ? { opacity: opacity / 100 } : {}),
                ...(hasTransform ? { transform: `rotate(${rotate}deg) scale(${zoom / 100})` } : {}),
              } : undefined;
              return (
                <div className="w-full relative overflow-hidden" data-footer-image-block>
                  <OptimizedImage
                    src={studio.footerImageUrl!}
                    alt={`${studio.shortName} decorative art`}
                    sizes="100vw"
                    width={1200}
                    height={280}
                    data-testid="img-footer-decor"
                    className="w-full h-[200px] md:h-[280px] object-cover object-center"
                    style={imgStyle}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black via-black/50 to-transparent pointer-events-none" />
                </div>
              );
            })()}
            <div className="container mx-auto px-6 pt-8 border-t border-white/5">
              <div className="flex flex-col items-center gap-4">
                <div className="text-center footer-legal">
                  <div className="mb-5">
                    <Link href="/netglowup" data-testid="link-footer-netglowup" className="inline-block bg-purple-600 hover:bg-purple-500 text-white text-lg md:text-xl font-semibold font-subheading tracking-wide px-5 py-2.5 rounded-full transition-colors shadow-[0_0_20px_rgba(147,51,234,0.3)]">
                      Need A Business Website? It's time for a NetGlowUp!
                    </Link>
                  </div>
                  <div className="flex flex-wrap justify-center gap-4 mb-3">
                    <Link href="/privacy" data-testid="link-footer-privacy" className="text-muted-foreground uppercase tracking-widest font-subheading hover:text-accent transition-colors">
                      Privacy Policy
                    </Link>
                    <span className="text-white/20">|</span>
                    <Link href="/terms" data-testid="link-footer-terms" className="text-muted-foreground uppercase tracking-widest font-subheading hover:text-accent transition-colors">
                      Terms of Service
                    </Link>
                    <span className="text-white/20">|</span>
                    <Link href="/accessibility" data-testid="link-footer-accessibility" className="text-muted-foreground uppercase tracking-widest font-subheading hover:text-accent transition-colors">
                      Accessibility
                    </Link>
                  </div>
                  <p className="text-muted-foreground uppercase tracking-widest font-subheading">
                    &copy; {new Date().getFullYear()} {studio.name}.
                  </p>
                  <p className="text-muted-foreground uppercase tracking-widest font-subheading mt-3">
                    All rights reserved.
                  </p>
                  {buildInfo && (
                    <Link href="/status" data-testid="link-footer-status" className="text-[10px] text-white/50 mt-5 font-mono tracking-wider hover:text-accent transition-colors block">
                      v{buildInfo.version} &middot; Build {buildInfo.buildId} &middot; {new Date(buildInfo.date).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric", hour: "2-digit", minute: "2-digit" })}
                    </Link>
                  )}
                </div>
              </div>
            </div>
          </>
        )}
      </footer>

      {!isAdminPage && !isAgency && (
        <div style={{ opacity: footerInView ? 0 : 1, pointerEvents: footerInView ? "none" : "auto", transition: "opacity 0.25s ease" }}>
          <EmailSignup />
        </div>
      )}

      {!isAdminPage && !isAgency && <div className="fixed right-6 z-[45] flex flex-col gap-4" style={{ bottom: "calc(1.5rem + var(--bottom-banner-h, 0px))", transition: "bottom 0.2s ease, opacity 0.25s ease", opacity: (footerInView || menuOpen) ? 0 : 1, pointerEvents: (footerInView || menuOpen) ? "none" : "auto" }}>
        <AnimatePresence>
          {showScrollTop && (
            <motion.button
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.8 }}
              transition={{ duration: 0.2 }}
              onClick={scrollToTop}
              aria-label="Scroll to top"
              data-testid="button-scroll-top"
              className="bg-pink-600 hover:bg-pink-500 text-white flex items-center justify-center w-14 h-14 rounded-full shadow-lg transition-colors"
            >
              <ArrowUp size={24} />
            </motion.button>
          )}
        </AnimatePresence>
        <AnimatePresence>
          {showScrollTop && (
            import.meta.env.DEV ? (
              <motion.button
                key="fab-phone"
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.8 }}
                transition={{ duration: 0.2 }}
                onClick={(e) => {
                  const now = Date.now();
                  emailTapRef.current = emailTapRef.current.filter(t => now - t < 600);
                  emailTapRef.current.push(now);
                  if (emailTapRef.current.length >= 3) {
                    emailTapRef.current = [];
                    e.preventDefault();
                    window.dispatchEvent(new Event("dev-show-pwa-prompt"));
                  } else {
                    window.location.href = `tel:${studio.phone}`;
                  }
                }}
                aria-label="Call the shop"
                data-testid="button-fab-phone"
                className="bg-green-600 hover:bg-green-500 text-white flex items-center justify-center w-14 h-14 rounded-full shadow-lg transition-colors"
              >
                <Phone size={24} />
              </motion.button>
            ) : (
              <motion.a
                key="fab-phone"
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.8 }}
                transition={{ duration: 0.2 }}
                href={`tel:${studio.phone}`}
                aria-label="Call the shop"
                data-testid="button-fab-phone"
                className="bg-green-600 hover:bg-green-500 text-white flex items-center justify-center w-14 h-14 rounded-full shadow-lg transition-colors"
              >
                <Phone size={24} />
              </motion.a>
            )
          )}
        </AnimatePresence>
        <AnimatePresence>
          {showScrollTop && !emailSubscribed && (
            <motion.button
              key="fab-email"
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.8 }}
              transition={{ duration: 0.2 }}
              onClick={() => window.dispatchEvent(new Event("open-email-signup"))}
              aria-label={emailWidgetOpen ? "Close email signup" : "Open email signup"}
              data-testid="button-fab-email"
              className="relative bg-accent hover:bg-accent/80 text-white flex items-center justify-center w-14 h-14 rounded-full shadow-lg transition-colors cursor-pointer"
            >
              {emailWidgetOpen ? <MailOpen size={24} /> : <Mail size={24} />}
              <span className="absolute -top-0.5 -right-0.5 w-3.5 h-3.5 bg-red-500 rounded-full border-2 border-black animate-pulse" />
            </motion.button>
          )}
        </AnimatePresence>
        <AnimatePresence>
          {showScrollTop && isFlagEnabled("studio_assistant_enabled") && (
            <motion.div
              key="fab-chat"
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.8 }}
              transition={{ duration: 0.2 }}
            >
              <Suspense fallback={null}><HomeChatWidget /></Suspense>
            </motion.div>
          )}
        </AnimatePresence>
      </div>}
      <OfflineIndicator />
      {isAdminPage && !isAgency && !location.startsWith("/artist-portal") && !location.startsWith("/receptionist-portal") && !location.startsWith("/agency-portal") && (
        <Suspense fallback={null}>
          <AdminHelpWidget
            open={helpOpen}
            onOpenChange={setHelpOpen}
            onBadgeCountChange={setHelpBadgeCount}
          />
        </Suspense>
      )}
      {/* INC-2026-05-10: SW update banner — replaces the old auto-reload.
          Shown when a new service worker has taken control after a deployment.
          User chooses when to reload; no more mid-session forced refreshes. */}
      <AnimatePresence>
        {swUpdateReady && (
          <motion.div
            initial={{ y: 80, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 80, opacity: 0 }}
            transition={{ duration: 0.25, ease: "easeOut" }}
            className="fixed bottom-4 left-1/2 -translate-x-1/2 z-[60] flex items-center gap-3 bg-black border border-accent/50 text-white text-sm font-subheading uppercase tracking-widest px-5 py-3 rounded-full shadow-[0_0_24px_rgba(139,92,246,0.35)] whitespace-nowrap"
            role="status"
            aria-live="polite"
            data-testid="banner-sw-update"
          >
            <span className="w-2 h-2 rounded-full bg-accent animate-pulse shrink-0" />
            <span className="text-white/80">New version available</span>
            <button
              onClick={() => window.location.reload()}
              className="ml-1 text-violet-300 hover:text-white transition-colors font-bold"
              data-testid="button-sw-reload"
            >
              Reload
            </button>
            <button
              onClick={() => setSwUpdateReady(false)}
              aria-label="Dismiss"
              className="ml-1 text-white/40 hover:text-white transition-colors text-base leading-none"
              data-testid="button-sw-dismiss"
            >
              ×
            </button>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
